import LogoutIcon from '@mui/icons-material/Logout';
import QrCodeIcon from '@mui/icons-material/QrCode';
import { Avatar, Box, CircularProgress, IconButton } from '@mui/material';
import BackendAuthHandler from 'BackendAuthHandler';
import BackendService from 'BackendService';
import { CommonLoggedInDataContext, CommonLoggedInDataWithToken } from 'CommonLoggedInData';
import HeaderBrandLockup from 'HeaderBrandLockup';
import HeaderCenterContent from 'HeaderCenterContent';
import React from 'react';
import Sidebar from 'Sidebar';
import ContactDetails from '../models/ContactDetails';
import './Header.scss';
import { withRouter } from './helpers/withRouter';
import { Palette } from 'Palette';
import { StyledAppBar } from './styles/StyledAppBar';
import { StyledToolbar } from './styles/StyledToolbar';
import { LogoContainer, StyledMenu, StyledMenuItem } from './styles/styles';

interface HeaderProps {
  navigate: (path: string) => void;
  avatarBgColor?: string;
}

class Header extends React.Component<
  HeaderProps,
  {
    loading: boolean;
    userInitials?: string;
    anchorEl: HTMLElement | null;
    impersonating: boolean;
  }
> {
  static contextType = CommonLoggedInDataContext;

  constructor(props: HeaderProps) {
    super(props);
    this.state = {
      loading: true,
      anchorEl: null,
      impersonating: false,
    };
  }

  componentDidMount() {
    new BackendService()
      .getUserNameAndInitials()
      .then((data: ContactDetails & { impersonating: boolean }) => {
        this.setState({
          loading: false,
          userInitials: data.userInitials,
          impersonating: data.impersonating,
        });
      });
  }

  handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    this.setState({ anchorEl: event.currentTarget });
  };

  handleCloseMenu = () => {
    this.setState({ anchorEl: null });
  };

  handleLogout = () => {
    this.handleCloseMenu();
    new BackendAuthHandler().logout();
  };

  handleGenerateQR = () => {
    this.handleCloseMenu();
    this.props.navigate('/settings?tab=cross-device');
  };

  render() {
    const { anchorEl, userInitials, loading } = this.state;

    return (
      <StyledAppBar
        elevation={0}
        position="fixed"
        paddingX={0}
        className="header-container hide-print"
        sx={(theme) => {
          const context = this.context as CommonLoggedInDataWithToken;
          const sidebarWidth = context.sidebarExpanded ? 240 : 64;

          return {
            width: {
              xs: '100%',
              md: `calc(100% - ${sidebarWidth}px)`,
            },
            marginLeft: {
              xs: 0,
              md: `${sidebarWidth}px`,
            },
            '&.header-container': {
              mr: 0,
            },
            boxSizing: 'border-box',
            transition: theme.transitions.create(['width', 'margin'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.standard,
            }),
          };
        }}
      >
        <StyledToolbar sx={{ px: { xs: '1rem', md: '1.875rem' } }}>
          <LogoContainer>
            <img src="/assets/fides-logo.svg" alt="Fides Logo" style={{ height: 40 }} />
          </LogoContainer>
          <HeaderCenterContent />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2.5 } }}>
            <HeaderBrandLockup />
            <IconButton
              className="header-avatar"
              onClick={this.handleAvatarClick}
              sx={{
                p: 0.5,
                pr: 0,
                alignSelf: 'center',
                xs: { display: 'none' },
              }}
            >
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  fontSize: '0.75rem',
                  lineHeight: 1,
                  bgcolor: this.props.avatarBgColor || Palette.tone4.background,
                  color: this.props.avatarBgColor ? '#fff' : Palette.tone4.text,
                  display: { xs: 'none', md: 'flex' },
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {loading ? <CircularProgress size={18} /> : userInitials}
              </Avatar>
            </IconButton>
          </Box>{' '}
          <StyledMenu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={this.handleCloseMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <StyledMenuItem onClick={this.handleGenerateQR}>
              <QrCodeIcon fontSize="small" />
              Generate QR for Phone
            </StyledMenuItem>
            <StyledMenuItem onClick={this.handleLogout}>
              <LogoutIcon fontSize="small" />
              Logout
            </StyledMenuItem>
          </StyledMenu>{' '}
          <Sidebar mobile={true} />
        </StyledToolbar>
      </StyledAppBar>
    );
  }
}

export default withRouter(Header);
